import { useQuery } from "@tanstack/react-query";
import { getOrgUsageGraphData } from "@/helpers/apis";
import {
  Card,
  CardContent,
  CardHeader,
  Separator,
  Typography,
} from "@altimateai/lego";
import { Spinner, Stack } from "@altimateai/lego";
import { ComponentLoader } from "@/components/loader";
import { OrgCreditsUsageHistory } from "@/pages/credits/components/OrgCreditsUsageHistory";
import { UsageTrendChart } from "@/pages/credits/components/UsageTrendChart";
import { TopAITeammatesCard } from "@/pages/credits/components/TopAITeammatesCard";
import { TopUsersCard } from "@/pages/credits/components/TopUsersCard";
import { useDateFromSearchParams } from "@/helpers/useTimeHooks";
import dayjs from "dayjs";
import { DateRange } from "@/components/dateRange";
import { CREDITS_DATE_PREFIX } from "../constants";

export const OrgUsageTab = () => {
  const { startDate, endDate, onUpdate } = useDateFromSearchParams(
    dayjs().subtract(30, "day").toDate(),
    dayjs().toDate(),
    CREDITS_DATE_PREFIX
  );
  const { data: trendsData, isLoading: isTrendsDataLoading } = useQuery({
    queryKey: ["org-usage", startDate, endDate],
    queryFn: () =>
      getOrgUsageGraphData({
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString(),
      }),
  });

  if (isTrendsDataLoading) {
    return <ComponentLoader label="Loading organization usage data..." />;
  }

  if (!trendsData) {
    return (
      <div className="al-text-center al-text-gray-500 al-py-8">
        No organization usage data available
      </div>
    );
  }

  return (
    <div className="al-space-y-6">
      <Card>
        <CardHeader>
          <Stack align={"center"} justify={"between"}>
            <Typography variant={"section-heading"}>Usage Analytics</Typography>
            <DateRange
              startDate={startDate}
              endDate={endDate}
              onDateRangeSelect={(start, end) => {
                onUpdate(start, end);
              }}
            />
          </Stack>
        </CardHeader>
        <Separator />
        <CardContent>
          {isTrendsDataLoading ? (
            <Stack justify={"center"} align={"center"} className={"al-min-h-80"} gap={"lg"}>
              <Spinner size={"lg"} />
              <span>Loading...</span>
            </Stack>
          ) : (
            <Stack gap={"lg"} className={"al-w-full al-mt-4 al-max-h-80"}>
              <Stack
                gap={"lg"}
                direction={"column"}
                className={"al-p-3 al-border al-rounded-md al-w-full"}
              >
                <Typography variant={"caption"}>Usage Trend</Typography>
                <UsageTrendChart
                  data={trendsData?.graph_data ?? []}
                  startDate={startDate}
                  endDate={endDate}
                />
              </Stack>
              <Stack
                gap={"lg"}
                direction={"column"}
                className={"al-p-3 al-border al-rounded-md al-w-full"}
              >
                <Typography variant={"caption"}>Top Users</Typography>
                <TopUsersCard
                  users={trendsData?.top_users.users ?? []}
                  totalCredits={trendsData?.top_users.total_credits_consumed ?? 0}
                />
              </Stack>
              <Stack
                gap={"lg"}
                direction={"column"}
                className={"al-p-3 al-border al-rounded-md al-w-full"}
              >
                <Typography variant={"caption"}>Top AI Teammates</Typography>
                <TopAITeammatesCard
                  teammates={trendsData?.top_ai_teammates.teammates ?? []}
                  totalCredits={trendsData?.top_ai_teammates.total_credits_consumed ?? 0}
                />
              </Stack>
            </Stack>
          )}
        </CardContent>
      </Card>
      <OrgCreditsUsageHistory />
    </div>
  );
};
